import type { FC } from "react";
import { useMemo } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  XAxis,
  YAxis,
} from "recharts";

import { useGetPatientHistoryByAttribute } from "@/api/history";
import ChartTooltipComponent from "@/components/patient/chart-tooltip";
import type { ChartConfig } from "@/components/ui/chart";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import Loader from "@/components/ui/loader";
import type { HistoryAttribute } from "@/types/api/history";
import { getHistoryAttributeLabel } from "@/utils/patient";

type PatientHistoryProps = {
  attribute: HistoryAttribute;
  patientId: number;
};

const ATTRIBUTE_TO_FIELD_MAP: Record<
  HistoryAttribute,
  "dailySmokes" | "hba1c" | "stress" | "plaqueIndex" | "bleedingIndex"
> = {
  CIGARETTES: "dailySmokes",
  DIABETES: "hba1c",
  STRESS: "stress",
  IP: "plaqueIndex",
  IS: "bleedingIndex",
};
const CIGARETTES_THRESHOLD = 10;
const DIABETES_THRESHOLD = 7;
const STRESS_THRESHOLD = 5;
const IP_THRESHOLD = 20;
const IS_THRESHOLD = 20;

const PatientHistory: FC<PatientHistoryProps> = ({ attribute, patientId }) => {
  const { isLoading, data: patientHistory } = useGetPatientHistoryByAttribute(
    attribute,
    patientId
  );

  const threshold = useMemo(() => {
    switch (attribute) {
      case "CIGARETTES":
        return CIGARETTES_THRESHOLD;
      case "DIABETES":
        return DIABETES_THRESHOLD;
      case "STRESS":
        return STRESS_THRESHOLD;
      case "IP":
        return IP_THRESHOLD;
      case "IS":
        return IS_THRESHOLD;
      default:
        return 10;
    }
  }, [attribute]);

  const chartData = useMemo(() => {
    // Map all patient history items to chart data points with their actual dates
    const dataPoints: Array<{
      date: number; // timestamp
      dateObj: Date; // keep Date object for tooltip
      value: number;
      historyAttribute: HistoryAttribute;
    }> = [];

    if (patientHistory) {
      patientHistory.forEach((item) => {
        const value = item[ATTRIBUTE_TO_FIELD_MAP[attribute]];
        if (value !== null && value !== undefined) {
          const dateObj = new Date(item.createdAt);
          dataPoints.push({
            date: dateObj.getTime(), // timestamp for X axis
            dateObj, // Date object for tooltip
            value,
            historyAttribute: attribute,
          });
        }
      });
    }

    // Sort by date to ensure proper line rendering
    return dataPoints.sort((a, b) => a.date - b.date);
  }, [patientHistory, attribute]);

  // Calculate min and max values based on attribute rules
  const getMinMaxValues = (
    values: Array<number>,
    historyAttribute: HistoryAttribute
  ): { min: number; max: number } => {
    const min = 0; // Always 0

    let max: number;
    const dataMax = Math.max(...values);
    switch (historyAttribute) {
      case "STRESS":
        max = 10;
        break;
      case "CIGARETTES":
        max = values.length > 0 ? dataMax + 10 : 40;
        break;
      case "IP":
      case "IS":
        max = 100;
        break;
      case "DIABETES":
        max = values.length > 0 ? dataMax + 2 : 10;
        break;
      default:
        max = 100;
    }

    return { min, max };
  };

  // Get ticks for the y-axis
  const getTicks = (
    minValue: number,
    maxValue: number,
    historyAttribute: HistoryAttribute
  ): Array<string> => {
    switch (historyAttribute) {
      // 10 by 10 until maxValue + 10 is reached
      case "CIGARETTES":
        return Array.from(
          { length: Math.ceil((maxValue + 10) / 10) },
          (_, index) => (index * 10).toString()
        );
      // 2 by 2 until maxValue + 2 is reached
      case "DIABETES":
        return Array.from(
          { length: Math.ceil((maxValue + 2) / 2) },
          (_, index) => (index * 2).toString()
        );
      case "STRESS":
        return ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];
      case "IP":
      case "IS":
        return [
          "0",
          "10",
          "20",
          "30",
          "40",
          "50",
          "60",
          "70",
          "80",
          "90",
          "100",
        ];
      default:
        return [minValue.toString(), threshold.toString(), maxValue.toString()];
    }
  };

  const {
    minValue,
    maxValue,
    gradientId,
    gradientStops,
    ticks,
    xAxisDomain,
    xAxisTicks,
  } = useMemo(() => {
    const values = chartData
      .map((d) => d.value)
      .filter((v) => v !== null) as Array<number>;

    const { min, max } = getMinMaxValues(values, attribute);

    // Calculate threshold position as percentage of the Y-axis range
    // Had to adapt range calculation because the gradient is applied only for displayed values
    const range = values.length > 0 ? Math.max(...values) - min : 0;
    const thresholdPosition =
      range > 0 ? ((threshold - min) / range) * 100 : 50;

    // Ensure valid range [0, 100]
    const clampedThresholdPosition = Math.max(
      0,
      Math.min(100, thresholdPosition)
    );

    // Build gradient stops array
    const stops: Array<{ offset: string; color: string; opacity: number }> = [];

    // Green at bottom
    stops.push({ offset: "0%", color: "#17A600", opacity: 0.4 });

    // Transition before threshold
    if (clampedThresholdPosition > 0) {
      const beforeThreshold = Math.max(0, clampedThresholdPosition - 3);
      if (beforeThreshold > 0) {
        stops.push({
          offset: `${beforeThreshold}%`,
          color: "#7FC800",
          opacity: 0.4,
        });
      }
    }

    // Orange at threshold
    stops.push({
      offset: `${clampedThresholdPosition}%`,
      color: "#FFA500",
      opacity: 0.4,
    });

    // Transition after threshold
    if (clampedThresholdPosition < 100) {
      const afterThreshold = Math.min(100, clampedThresholdPosition + 3);
      if (afterThreshold < 100) {
        stops.push({
          offset: `${afterThreshold}%`,
          color: "#FF6B00",
          opacity: 0.4,
        });
      }
    }

    // Red at top
    stops.push({ offset: "100%", color: "#D20000", opacity: 0.4 });

    // Calculate year for X axis (use first data point year or current year)
    const year =
      chartData.length > 0
        ? new Date(chartData[0].date).getFullYear()
        : patientHistory && patientHistory.length > 0
          ? new Date(patientHistory[0]?.createdAt).getFullYear()
          : new Date().getFullYear();

    return {
      minValue: min,
      maxValue: max,
      gradientId: `gradient-${attribute}-${patientId}`,
      gradientStops: stops,
      ticks: getTicks(min, max, attribute),
      xAxisDomain: [
        new Date(year, 0, 1).getTime(), // January 1st
        new Date(year, 11, 31).getTime(), // December 31st
      ],
      xAxisTicks: Array.from(
        { length: 12 },
        (_, index) => new Date(year, index, 1).getTime() // first day of each month
      ),
    };
  }, [chartData, threshold, attribute, patientId]);

  const chartConfig = {
    value: {
      label: "",
      color: "var(--chart-1)",
    },
  } satisfies ChartConfig;

  return isLoading ? (
    <Loader />
  ) : (
    <div>
      <h3 className="text-lg font-medium">
        {getHistoryAttributeLabel(attribute)}
      </h3>
      <ChartContainer config={chartConfig} style={{ height: 400 }}>
        <AreaChart
          accessibilityLayer
          data={chartData}
          margin={{
            left: 12,
            right: 12,
            top: 12,
            bottom: 12,
          }}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="1" x2="0" y2="0">
              {gradientStops.map((stop, index) => (
                <stop
                  key={index}
                  offset={stop.offset}
                  stopColor={stop.color}
                  stopOpacity={stop.opacity}
                />
              ))}
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="date"
            type="number"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            domain={xAxisDomain}
            ticks={xAxisTicks}
            tickFormatter={(value) => {
              const date = new Date(value);
              return date.toLocaleDateString("fr-FR", { month: "short" });
            }}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            domain={[minValue, maxValue]}
            ticks={ticks}
          />
          <ReferenceLine y={threshold} stroke="#666" strokeDasharray="3 3" />
          <ChartTooltip
            content={(tooltipProps) => (
              <ChartTooltipContent
                active={tooltipProps.active}
                payload={tooltipProps.payload}
                label={tooltipProps.label}
                labelFormatter={tooltipProps.labelFormatter}
                labelClassName={tooltipProps.labelClassName}
                accessibilityLayer={tooltipProps.accessibilityLayer}
                coordinate={tooltipProps.coordinate}
                activeIndex={tooltipProps.activeIndex}
                allowEscapeViewBox={tooltipProps.allowEscapeViewBox}
                contentStyle={tooltipProps.contentStyle}
                wrapperStyle={tooltipProps.wrapperStyle}
                className="p-0"
                hideLabel
                formatter={(value, _name, item) => (
                  <ChartTooltipComponent
                    attribute={attribute}
                    value={value as number}
                    date={item.payload?.dateObj}
                    threshold={threshold}
                  />
                )}
              />
            )}
            cursor={false}
            defaultIndex={1}
          />
          <Area
            dataKey="value"
            type="linear"
            fill={`url(#${gradientId})`}
            stroke="#666"
            strokeWidth={2}
          />
        </AreaChart>
      </ChartContainer>
    </div>
  );
};

export default PatientHistory;
